import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useRef } from 'react';
import {
  RowSelectionConfig,
  TableAction,
} from '../../../../../services/Main/types.Component';
import mainService from '../../../../../services/Main';
import { ActionServerResponse } from '../../../../../services/Main/types';

interface SelectionResponse {
  actions: TableAction[];
}

export const useSelectionActions = (
  selectedRowIds: (string | number)[],
  rowSelectionConfig?: RowSelectionConfig
) => {
  const [isSelectionLoading, setIsSelectionLoading] = React.useState(false);
  const [selectionRowActions, setSelectionRowActions] = React.useState<
    TableAction[]
  >([]);

  const previousSelectionRef = useRef<Array<string | number>>([]);
  const requestIdRef = useRef(0);

  // Хак для получения актуального состояния selectedRowIds через ref, чтобы не пересоздавалась debounce функция
  const selectionRef = useRef<Array<string | number>>([]);
  useEffect(() => {
    selectionRef.current = selectedRowIds;
  }, [selectedRowIds]);

  const loadActions = () => {
    if (!rowSelectionConfig) return;

    const curr = selectionRef.current;
    const prev = previousSelectionRef.current;

    // Вычисляем diff через Set (O(n))
    const prevSet = new Set<string | number>(prev);
    const currSet = new Set<string | number>(curr);

    const addedIds: Array<string | number> = [];
    Array.from(currSet).forEach((id) => {
      if (!prevSet.has(id)) {
        addedIds.push(id);
      }
    });

    const removedIds: Array<string | number> = [];
    Array.from(prevSet).forEach((id) => {
      if (!currSet.has(id)) {
        removedIds.push(id);
      }
    });

    // Проверяем, есть ли реальные изменения
    const hasChanges = addedIds.length > 0 || removedIds.length > 0;

    // Обновляем previousSelectionRef только если есть изменения
    if (hasChanges) {
      previousSelectionRef.current = [...curr];
    }

    // Если все ID сняты, очищаем действия и не отправляем запрос
    if (curr.length === 0) {
      setSelectionRowActions([]);
      setIsSelectionLoading(false);
      return;
    }

    // Если нет изменений, просто сбрасываем индикатор загрузки
    if (!hasChanges) {
      setIsSelectionLoading(false);
      return;
    }

    setIsSelectionLoading(true);

    requestIdRef.current += 1;
    const thisRequestId = requestIdRef.current;

    mainService
      .makeActionRequest<SelectionResponse>(rowSelectionConfig.requestConfig, {
        allIds: curr,
        addedIds,
        removedIds,
      })
      .then((response: ActionServerResponse<SelectionResponse>) => {
        // применяем ответ ТОЛЬКО если это самый последний запрос
        if (response.isSuccess && thisRequestId === requestIdRef.current) {
          setSelectionRowActions(response?.payload?.actions || []);
        }
      })
      .finally(() => {
        // сбрасываем индикатор только для «последнего» запроса
        if (thisRequestId === requestIdRef.current) {
          setIsSelectionLoading(false);
        }
      });
  };

  const debounceLoad = useCallback(debounce(loadActions, 650), [
    rowSelectionConfig,
  ]);

  useEffect(() => {
    // Не очищаем действия сразу, только устанавливаем индикатор загрузки
    // если есть выбранные строки
    if (selectedRowIds.length > 0) {
      setIsSelectionLoading(true);
      debounceLoad();
    } else {
      setIsSelectionLoading(false);
      setSelectionRowActions([]);
      previousSelectionRef.current = [];
    }

    return () => {
      debounceLoad.cancel();
    };
  }, [selectedRowIds, debounceLoad, rowSelectionConfig]);

  return {
    isSelectionLoading,
    selectionRowActions,
  };
};
